import { Link, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { TwoFactorChallenge } from '@nucleus-commerce/api-client/platform';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ServerError } from '@/shared/ui/server-error';

import { useVerifyMagicLink } from '../hooks/use-verify-magic-link';
import { isTwoFactorChallenge, type ChallengeEnd } from '../schemas/two-factor-rules';

import { AuthCard } from './auth-frame';
import { TwoFactorStep } from './two-factor-step';

export interface MagicLinkCardProps {
  token: string | undefined;
}

/**
 * Exchanges the emailed sign-in token once per page (the ref survives StrictMode's second effect
 * run, and the token is single use). A 202 continues with the same code step as the password
 * sign-in; a 401 means the link is invalid, used or older than 15 minutes.
 */
export function MagicLinkCard({ token }: MagicLinkCardProps) {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const verify = useVerifyMagicLink();
  const { mutate } = verify;
  const started = useRef<string | null>(null);
  const [challengeEnd, setChallengeEnd] = useState<ChallengeEnd | null>(null);
  const result = verify.data;
  const challenge: TwoFactorChallenge | null =
    result && isTwoFactorChallenge(result) && !challengeEnd ? result : null;
  const signedIn = result !== undefined && !isTwoFactorChallenge(result);

  const enter = useCallback(() => navigate({ to: '/', replace: true }), [navigate]);
  const toSignIn = useCallback(() => navigate({ to: '/login' }), [navigate]);
  const onExpired = useCallback((reason: ChallengeEnd) => setChallengeEnd(reason), []);

  useEffect(() => {
    if (!token || started.current === token) return;
    started.current = token;
    mutate({ token });
  }, [token, mutate]);

  useEffect(() => {
    if (signedIn) void enter();
  }, [signedIn, enter]);

  const requestNew = (
    <Button asChild className="w-full">
      <Link to="/sign-in-link">{t('magicLink.requestNew')}</Link>
    </Button>
  );

  if (!token) {
    return (
      <AuthCard id="magic-link" title={t('magicLink.invalidTitle')}>
        <div className="flex flex-col gap-4">
          <p className="text-muted-foreground text-sm">{t('magicLink.missingToken')}</p>
          {requestNew}
        </div>
      </AuthCard>
    );
  }

  if (challenge) {
    return (
      <AuthCard id="magic-link" title={t('twoFactor.title')}>
        <TwoFactorStep
          challenge={challenge}
          onVerified={enter}
          onBack={() => void toSignIn()}
          onExpired={onExpired}
          backLabel={t('frame.backToSignIn')}
        />
      </AuthCard>
    );
  }

  if (challengeEnd) {
    return (
      <AuthCard id="magic-link" title={t(`twoFactor.${challengeEnd}Title`)}>
        <div className="flex flex-col gap-4">
          <p role="alert" className="text-muted-foreground text-sm">
            {t(
              challengeEnd === 'expired'
                ? 'magicLink.challengeExpired'
                : 'magicLink.challengeInvalid',
            )}
          </p>
          {requestNew}
        </div>
      </AuthCard>
    );
  }

  if (verify.isError) {
    const invalid = isApiError(verify.error) && verify.error.kind === 'unauthorized';
    return (
      <AuthCard
        id="magic-link"
        title={invalid ? t('magicLink.invalidTitle') : t('magicLink.failedTitle')}
      >
        <div className="flex flex-col gap-4">
          {invalid ? (
            <p className="text-muted-foreground text-sm">{t('magicLink.invalid')}</p>
          ) : (
            <ServerError error={verify.error} />
          )}
          {requestNew}
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard id="magic-link" title={t('magicLink.signingInTitle')}>
      <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" aria-hidden />
        {t('magicLink.signingIn')}
      </p>
    </AuthCard>
  );
}
